/** 会话上下文：当前身份、可切换的组织、登出。 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { request, setTokens, setUnauthorizedHandler, getTokens } from './api.ts';
import { roleAtLeast } from './format.ts';
import type { Me, OrgRole, ScopedChannel } from './types.ts';

interface SessionValue {
  readonly me: Me;
  readonly reload: () => void;
  readonly logout: () => void;
  readonly switchOrg: (orgId: string) => Promise<void>;
  /** 当前身份是否满足角色门槛。写操作按钮用它决定禁用与否。 */
  readonly can: (minimum: OrgRole) => boolean;
  readonly isPlatform: boolean;
  readonly isAgent: boolean;
  readonly isMerchant: boolean;
  /**
   * 当前身份是否被收窄到指定通道。
   *
   * 界面用它做两件事，都不是安全措施（真正的授权在服务端）：
   * 把「为什么少」讲清楚，以及别让人点了才吃 403。
   */
  readonly isChannelScoped: boolean;
  /** 白名单里的通道；不受约束时为空数组。 */
  readonly scopedChannels: readonly ScopedChannel[];
  /**
   * 当前身份看不到利润结构（成本/费率/分润/结算金额）。
   *
   * 界面用它隐藏「费率」「资金」入口，并且**不去请求那些必然 403 的接口** ——
   * 总览页的分润、商户榜、账本三块就是靠它跳过的，否则页面上会挂三个错误框。
   */
  readonly isMoneyHidden: boolean;
  /**
   * 可见页面白名单。`null` = 不受限；非空数组 = 只画这些入口。
   *
   * 与上面两个方向相反：它们是「命中就藏」，这个是「不在名单里就藏」。
   */
  readonly pageScope: readonly string[] | null;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (value === null) {
    throw new Error('useSession 必须在 SessionProvider 内使用');
  }
  return value;
}

export function SessionProvider({
  me, onReload, onLogout, children,
}: {
  me: Me;
  onReload: () => void;
  onLogout: () => void;
  children: ReactNode;
}) {
  const switchOrg = useCallback(async (orgId: string): Promise<void> => {
    const body = await request<{
      access_token: string;
      refresh_token: string;
      org_id: string;
    }>('/v1/auth/switch-org', { method: 'POST', body: { org_id: orgId } });
    setTokens({
      accessToken: body.access_token,
      refreshToken: body.refresh_token,
      orgId: body.org_id,
    });
    onReload();
  }, [onReload]);

  const value = useMemo<SessionValue>(() => ({
    me,
    reload: onReload,
    logout: onLogout,
    switchOrg,
    can: (minimum) => roleAtLeast(me.role, minimum),
    isPlatform: me.org.type === 'platform',
    isAgent: me.org.type === 'agent',
    isMerchant: me.org.type === 'merchant',
    isChannelScoped: me.channel_scope !== null,
    isMoneyHidden: me.money_hidden,
    pageScope: me.page_scope,
    scopedChannels: me.channel_scope ?? [],
  }), [me, onReload, onLogout, switchOrg]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * 启动时用 `/v1/auth/me` 探活。
 *
 * 不能只看 localStorage 里有没有令牌就当已登录：令牌可能已经过期、
 * 会话可能已经被踢（改密码、被移出组织、管理员 logout-all）。
 * 那样界面会先渲染出一个空壳，然后每个请求各自 401，用户看到一屏错误。
 */
export function useBootstrap(): {
  readonly me: Me | null;
  readonly loading: boolean;
  readonly reload: () => void;
  readonly logout: () => void;
} {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  /**
   * 退出登录。
   *
   * **必须先告诉服务端**，否则这个按钮什么都没注销：令牌只是从 localStorage 里消失了，
   * 服务端会话与那枚 14 天有效、可无限续期的 refresh token 依然完全可用。
   * 任何一份该令牌的副本（浏览器同步到别的设备、公用电脑、早先被插件抓走的）
   * 在用户主观上「已经退出」之后仍然能登进来。
   *
   * 服务端调用失败也照样清本地：登出是用户明确表达的意图，
   * 不能因为一次网络抖动就把人留在登录态里。
   */
  const logout = useCallback(() => {
    void request('/v1/auth/logout', { method: 'POST' })
      .catch(() => { /* 会话可能本来就没了；无论如何都要往下走 */ })
      .finally(() => {
        setTokens(null);
        setMe(null);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => { setMe(null); setLoading(false); });
    return () => { setUnauthorizedHandler(null); };
  }, []);

  useEffect(() => {
    if (getTokens() === null) {
      setMe(null);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    void request<Me>('/v1/auth/me')
      .then((result) => { if (alive) { setMe(result); setLoading(false); } })
      .catch(() => { if (alive) { setMe(null); setLoading(false); } });
    return () => { alive = false; };
  }, [nonce]);

  return {
    me,
    loading,
    reload: () => { setNonce((value) => value + 1); },
    logout,
  };
}
